import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { planning } from '../../../../api/integrations'
import ErrorBoundary from '../../../molecules/ErrorBoundary'
import SydLabel from '../../../commonDesign/SydLabel'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import { sum } from './utils'
import PLDCard from './PLDCard'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    '& p': {
      padding: 0,
      margin: 0
    },
    '& h1,h2,h3,h4,h5,h6': {
      padding: 0,
      margin: 0
    }
  },
  summary: {
    padding: 0,
    margin: 0,
    fontSize: '1rem',
    color: 'var(--color-neutral-800)'
  },
  netWorth: {
    display: 'flex',
    columnGap: '1.5rem',
    padding: '1rem 0',
    '& .__value': {
      fontSize: '1.6rem'
    }
  },
  result: {
    display: 'flex',
    columnGap: '1.5rem',
    padding: '1rem 0'
  }
})

function ProtectLiveDream ({
  clientId,
  excludeZeroValues,
  styles: propStyles,
  netWorth,
  showSummary,
  showBreakdown,
  assetTypeOverrides,
  seriesProps
}) {
  const defaultStyles = useStyles()
  const styles = {
    ...defaultStyles,
    ...(propStyles ?? defaultStyles)
  }

  netWorth = {
    ...(ProtectLiveDream.defaultProps.netWorth),
    ...(netWorth ?? {}),
    makeup: {
      ...(ProtectLiveDream.defaultProps.netWorth.makeup),
      ...(netWorth?.makeup ?? {})
    }
  }

  const { formatter } = useFormattingContext()

  const assetTypeOverridesMap = useMemo(() => {
    return (assetTypeOverrides ?? []).reduce((acc, curr) => {
      acc[curr.id] = curr
      return acc
    }, {})
  }, [assetTypeOverrides])

  const {
    data,
    isLoading
  } = planning.usePlanningQuery({
    queryType: 'protectlivedream',
    payload: { clientId }
  })

  const pageData = useMemo(() => {
    if (isLoading || !('organization' in (data ?? {}))) return {}

    const {
      organization: {
        supportingText,
        buckets
      },
      client = []
    } = data

    return {
      summary: supportingText,
      buckets: buckets.map((bucket, i) => {
        return {
          ...bucket,
          assetTypes: bucket.assetTypes.map(a => assetTypeOverridesMap?.[a.id] ?? a),
          client: client?.[i] ?? {}
        }
      }),
      netWorth: formatter(sum(client?.flatMap(c => c.assets), i => i.value), 'money')
    }
  }, [isLoading, data, formatter, assetTypeOverridesMap])

  const netWorthCardClass = useMemo(() => `opal-card-${netWorth?.cardSize}`, [netWorth])

  if (isLoading) return null

  return (
    <ErrorBoundary name='Protect, Live, Dream'>
      <div className={styles.container}>
        {showSummary && (
          <p className={styles.summary}>{pageData.summary}</p>
        )}
        {netWorth.show && (
          <div className={styles.netWorth}>
            <div className={netWorthCardClass} style={{ flex: netWorth.flex }}>
              <SydLabel label={netWorth.title} style={{ fontSize: netWorth.fontSize }} />
              <span className='__value'>${pageData.netWorth}</span>
            </div>
            <div className={netWorthCardClass} style={{ flex: netWorth.makeup.flex }}>
              <SydLabel label={netWorth.makeup.title} style={{ fontSize: netWorth.makeup.fontSize }} />
            </div>
          </div>
        )}
        <div className={styles.result}>
          {pageData.buckets?.map((bucket, i) => (
            <PLDCard
              key={i}
              showBreakdown={showBreakdown}
              assetTypeOverrides={assetTypeOverridesMap}
              assetTypes={bucket.assetTypes}
              assets={bucket.client?.assets ?? []}
              excludeZeroValues={excludeZeroValues}
              seriesProps={seriesProps}
            />
          ))}
        </div>
      </div>
    </ErrorBoundary>
  )
}

ProtectLiveDream.propTypes = {
  clientId: PropTypes.number,
  excludeZeroValues: PropTypes.bool,
  styles: PropTypes.object,
  netWorth: PropTypes.shape({
    show: PropTypes.bool,
    cardSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    title: PropTypes.string,
    fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    makeup: PropTypes.shape({
      title: PropTypes.string,
      fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  }),
  assetTypeOverrides: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string
  })),
  seriesProps: PropTypes.object,
  showSummary: PropTypes.bool,
  showBreakdown: PropTypes.bool
}

ProtectLiveDream.defaultProps = {
  netWorth: {
    show: true,
    cardSize: 'md',
    title: 'NET WORTH >',
    fontSize: '.8rem',
    flex: 1,
    makeup: {
      title: 'Your Net Worth Makeup',
      fontSize: '1.2rem',
      flex: 2
    }
  },
  seriesProps: {},
  assetTypeOverrides: [],
  showSummary: true,
  showBreakdown: true,
  excludeZeroValues: false
}

export default ProtectLiveDream
